import { getAnalyticsContext } from '../../../../analytics';
import {
    getSessionUtmParameterName,
    getVisitorUtmParameterName,
} from '../../../../config/SiteConfigManager';
import { getConsentMode } from '../../../../services/consentMode';
import { logger } from '../../../../utils/core/logger';
import type { FormDetectionContext } from '../../types';
import type { PostMessageSubmission, PostMessageSubProvider } from './types';

/** Matches calendly.com and any subdomain (e.g., assets.calendly.com) */
const CALENDLY_HOST_RE = /(^|\.)calendly\.com$/;

/** Cookie name used by CookieProvider for cross-subdomain session tracking */
const LAST_ACTIVE_SESSION_COOKIE = 'rose_last_active_session';
const VISITOR_ID_COOKIE = 'rose_client_id';

/** Max retries when config isn't loaded yet at setup time */
const MAX_INIT_RETRIES = 10;
const RETRY_INTERVAL_MS = 500;

let linkObserver: MutationObserver | null = null;
let clickHandler: ((e: Event) => void) | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

interface CalendlyAttributionParamNames {
    sessionParamName: string | null;
    visitorParamName: string | null;
}

interface CalendlyAttributionValues extends CalendlyAttributionParamNames {
    sessionId: string | null;
    visitorId: string | null;
}

function readCookieValue(cookieName: string): string | null {
    if (getConsentMode() !== 'full') return null;
    try {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const trimmed = cookie.trim();
            const eqIdx = trimmed.indexOf('=');
            if (eqIdx === -1) continue;
            const name = trimmed.slice(0, eqIdx);
            const value = trimmed.slice(eqIdx + 1);
            if (name === cookieName) {
                return decodeURIComponent(value);
            }
        }
    } catch {
        // Cookie access blocked — skip
    }
    return null;
}

/**
 * Get attribution IDs from the best available source.
 * Prefers the analytics context (if populated), falls back to reading cookies directly.
 */
function getAttributionValues(
    paramNames: CalendlyAttributionParamNames
): CalendlyAttributionValues {
    const context = getAnalyticsContext();

    return {
        ...paramNames,
        sessionId: context.lastActiveSessionId ?? readCookieValue(LAST_ACTIVE_SESSION_COOKIE),
        visitorId: context.clientId ?? readCookieValue(VISITOR_ID_COOKIE),
    };
}

function hasAttributionValues(attribution: CalendlyAttributionValues): boolean {
    return Boolean(
        (attribution.sessionParamName && attribution.sessionId) ||
            (attribution.visitorParamName && attribution.visitorId)
    );
}

/**
 * Decorate a single anchor element with configured Rose attribution parameters.
 * Returns silently if the link is not a Calendly URL or is already decorated.
 */
function decorateLink(anchor: HTMLAnchorElement, attribution: CalendlyAttributionValues): void {
    if (getConsentMode() !== 'full') return;
    try {
        const url = new URL(anchor.href);
        if (!CALENDLY_HOST_RE.test(url.hostname)) return;

        let changed = false;
        if (attribution.sessionParamName && attribution.sessionId) {
            if (url.searchParams.get(attribution.sessionParamName) !== attribution.sessionId) {
                url.searchParams.set(attribution.sessionParamName, attribution.sessionId);
                changed = true;
            }
        }
        if (attribution.visitorParamName && attribution.visitorId) {
            if (url.searchParams.get(attribution.visitorParamName) !== attribution.visitorId) {
                url.searchParams.set(attribution.visitorParamName, attribution.visitorId);
                changed = true;
            }
        }

        if (!changed) return;
        anchor.href = url.toString();
    } catch {
        // Malformed href (e.g. "javascript:;") — skip silently
    }
}

/** Find and decorate all Calendly links within a root element. */
function decorateAllLinks(root: ParentNode, attribution: CalendlyAttributionValues): void {
    const anchors = root.querySelectorAll<HTMLAnchorElement>('a[href*="calendly.com"]');
    for (const anchor of anchors) {
        decorateLink(anchor, attribution);
    }
}

/**
 * Start link decoration, MutationObserver, and click capture handler.
 * Only requires configured param names — attribution IDs are read fresh on every
 * decoration because lastActiveSessionId is set when the visitor chats, which
 * often happens after page load (and after this activation).
 */
function activateDecoration(paramNames: CalendlyAttributionParamNames): void {
    // Clean up any previously registered listeners to prevent leaks on repeated calls
    if (clickHandler) {
        document.removeEventListener('click', clickHandler, true);
        clickHandler = null;
    }
    if (linkObserver) {
        linkObserver.disconnect();
        linkObserver = null;
    }

    // Decorate existing links if attribution is already available (returning visitor)
    const initialAttribution = getAttributionValues(paramNames);
    if (hasAttributionValues(initialAttribution)) {
        decorateAllLinks(document, initialAttribution);
    }

    // Click capture handler: just-in-time decoration right before navigation.
    // Ensures the link has the latest attribution even if the visitor chatted
    // after the page loaded.
    clickHandler = (e: Event) => {
        const anchor = (e.target as Element)?.closest?.('a[href*="calendly.com"]');
        if (!anchor) return;
        const attribution = getAttributionValues(paramNames);
        if (!hasAttributionValues(attribution)) return;
        decorateLink(anchor as HTMLAnchorElement, attribution);
    };
    document.addEventListener('click', clickHandler, true);

    // Watch for dynamically added or changed links
    linkObserver = new MutationObserver((mutations) => {
        const attribution = getAttributionValues(paramNames);
        if (!hasAttributionValues(attribution)) return;
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;
                    const el = node as Element;
                    if (
                        el.tagName === 'A' &&
                        (el as HTMLAnchorElement).href?.includes('calendly.com')
                    ) {
                        decorateLink(el as HTMLAnchorElement, attribution);
                    }
                    decorateAllLinks(el, attribution);
                }
            } else if (
                mutation.type === 'attributes' &&
                mutation.target instanceof HTMLAnchorElement
            ) {
                if (mutation.target.href?.includes('calendly.com')) {
                    decorateLink(mutation.target, attribution);
                }
            }
        }
    });

    linkObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['href'],
    });

    logger.debug('📅 CalendlyProvider: link decoration active', paramNames);
}

/**
 * Calendly scheduling embed detection + external link decoration.
 *
 * Calendly popup/inline embeds send postMessage events with:
 *   { event: "calendly.event_scheduled", payload: { event: { uri }, invitee: { uri } } }
 *
 * Events fired (all prefixed "calendly."):
 *   - profile_page_viewed, event_type_viewed — navigation
 *   - date_and_time_selected — user picks a slot
 *   - event_scheduled — booking confirmed (our submission signal)
 *   - page_height_resize — layout only, ignored
 *
 * Link decoration:
 *   When session_utm_name and/or visitor_utm_name is configured, the setup() hook
 *   decorates all <a> tags linking to calendly.com with Rose attribution IDs.
 *   This ensures external Calendly bookings carry the session/visitor for CRM
 *   attribution. A MutationObserver handles dynamically added links (Webflow, SPAs).
 *
 *   Because setup() can be called before config is loaded, the hook retries
 *   up to MAX_INIT_RETRIES times at RETRY_INTERVAL_MS intervals for the UTM
 *   param names. Attribution IDs are read fresh on every
 *   decoration — not captured at init — because the visitor typically chats
 *   with Rose after the page loads. A click capture handler ensures the link
 *   gets the latest IDs right before navigation.
 *
 * Docs: https://developer.calendly.com/api-docs/ZG9jOjI3ODM2MTAz-notifying-the-parent-window
 */
export const CalendlyProvider: PostMessageSubProvider = {
    name: 'calendly',
    trustedOrigins: /^https:\/\/([\w-]+\.)?calendly\.com$/,

    setup(context: FormDetectionContext): void {
        let retries = 0;

        const tryActivate = () => {
            if (getConsentMode() !== 'full') return;
            const paramNames = {
                sessionParamName: getSessionUtmParameterName(context.domain),
                visitorParamName: getVisitorUtmParameterName(context.domain),
            };

            if (!paramNames.sessionParamName && !paramNames.visitorParamName) {
                if (retries < MAX_INIT_RETRIES) {
                    retries++;
                    retryTimer = setTimeout(tryActivate, RETRY_INTERVAL_MS);
                    return;
                }
                logger.debug(
                    '📅 CalendlyProvider: no attribution UTM names configured, skipping link decoration'
                );
                return;
            }

            // Attribution IDs are read fresh on every decoration (click, mutation, etc.)
            // so we don't block activation on them being available yet.
            activateDecoration(paramNames);
        };

        tryActivate();
    },

    detect(data: Record<string, unknown>): boolean {
        return typeof data?.event === 'string' && (data.event as string).startsWith('calendly.');
    },

    isSubmission(data: Record<string, unknown>): boolean {
        return data?.event === 'calendly.event_scheduled';
    },

    extractData(data: Record<string, unknown>, origin: string): PostMessageSubmission {
        const payload = (data?.payload || {}) as Record<string, unknown>;
        const eventData = (payload?.event || {}) as Record<string, unknown>;
        const inviteeData = (payload?.invitee || {}) as Record<string, unknown>;

        const indicators = ['postmessage_calendly_booking_completed'];
        if (eventData?.uri) {
            indicators.push(`calendly_event_uri:${eventData.uri}`);
        }
        if (inviteeData?.uri) {
            indicators.push(`calendly_invitee_uri:${inviteeData.uri}`);
        }

        // Extract event ID from URI: .../scheduled_events/AAAA → AAAA
        const eventUri = (eventData?.uri as string) || '';
        const eventId = eventUri.split('/').pop();

        return {
            provider: 'calendly',
            formId: eventId ? `calendly_event_${eventId}` : 'calendly_booking',
            applyFormIdWhitelist: Boolean(eventId),
            indicators,
            origin,
        };
    },

    cleanup(): void {
        if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
        if (clickHandler) {
            document.removeEventListener('click', clickHandler, true);
            clickHandler = null;
        }
        if (linkObserver) {
            linkObserver.disconnect();
            linkObserver = null;
        }
    },
};
